import get from 'platform/utilities/data/get';
import { TOGGLE_NAMES } from 'platform/utilities/feature-toggles';

/**
 * Create a formData accessor for a given path.
 *
 * Returns the value at the provided path, or the fallback value when the path
 * resolves to null or undefined.
 *
 * @example
 * const sponsorRelationship = formValue('sponsor.relationship');
 * sponsorRelationship(formData);
 * // => 'spouse'
 *
 * @param {string} path - Dot-notation path in formData
 * @param {*} [fallback=undefined] - Value to return when no value exists
 * @returns {Function} Function that accepts formData and returns the value
 */
export const formValue =
  (path, fallback = undefined) =>
  formData =>
    get(path, formData) ?? fallback;

/**
 * Create a formData accessor for an indexed collection item.
 *
 * Returns the value at the provided path within the indexed collection item,
 * or the fallback value when the path resolves to null or undefined.
 *
 * @example
 * const applicantAge = indexedValue('applicants', 'age', 0);
 * applicantAge(formData, 1);
 * // => 23
 *
 * @param {string} collection - Array path in formData
 * @param {string} path - Property path within the collection item
 * @param {*} [fallback=undefined] - Value to return when no value exists
 * @returns {Function} Function that accepts formData and index and returns the value
 */
export const indexedValue =
  (collection, path, fallback = undefined) =>
  (formData, index) =>
    get(`${collection}.${index}.${path}`, formData) ?? fallback;

/**
 * Build form data view fields from feature toggle values.
 *
 * Each toggle is looked up by its camelCased name in `TOGGLE_NAMES` and
 * coerced to a boolean, so unknown or missing toggles resolve to `false`.
 *
 * @example
 * getToggleFormFields(state.featureToggles, [
 *   { toggleName: 'hcaRegOnlyEnabled', formKey: 'view:isRegOnlyEnabled' },
 * ]);
 * // => { 'view:isRegOnlyEnabled': true }
 *
 * @param {Object} toggleState - the `featureToggles` state slice
 * @param {{ toggleName: string, formKey: string }[]} toggles - toggle configs
 * @returns {Object} form data keys mapped to boolean toggle values
 */
export const getToggleFormFields = (toggleState, toggles) =>
  Object.fromEntries(
    toggles.map(({ toggleName, formKey }) => [
      formKey,
      Boolean(toggleState?.[TOGGLE_NAMES[toggleName]]),
    ]),
  );
